import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { User, Lock, Mail, Save, ArrowLeft, Eye, EyeOff, CreditCard, HelpCircle } from 'lucide-react';
import SubscriptionManagement from '../components/SubscriptionManagement';
import OnboardingModal from '../components/OnboardingModal';

const Profile = () => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');

  // État pour les informations personnelles
  const [profileData, setProfileData] = useState({
    nom: '',
    prenom: '',
    email: ''
  });

  // État pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // État pour la modale d'onboarding
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  // Référence pour rafraîchir les abonnements
  const [refreshSubscriptions, setRefreshSubscriptions] = useState(false);

  // Charger les données utilisateur au montage
  useEffect(() => {
    if (state.auth.user) {
      setProfileData({
        nom: state.auth.user.nom || '',
        prenom: state.auth.user.prenom || '',
        email: state.auth.user.email || ''
      });
    }
  }, [state.auth.user]);

  // Gérer les paramètres URL pour Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');

    if (success === 'true') {
      // Succès du paiement
      setActiveTab('subscription');
      actions.addNotification({
        type: 'success',
        message: 'Félicitations ! Votre abonnement a été activé avec succès.'
      });
      
      // Nettoyer les paramètres URL et rafraîchir les abonnements
      setSearchParams({});
      setRefreshSubscriptions(true);
    } else if (canceled === 'true') {
      // Paiement annulé
      setActiveTab('subscription');
      actions.addNotification({
        type: 'info',
        message: 'Le paiement a été annulé. Vous pouvez réessayer à tout moment.'
      });
      
      // Nettoyer les paramètres URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, actions]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await actions.updateProfile(profileData);
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.new_password !== passwordData.confirm_password) {
      actions.addNotification({ 
        type: 'error', 
        message: 'Les nouveaux mots de passe ne correspondent pas' 
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      actions.addNotification({ 
        type: 'error', 
        message: 'Le mot de passe doit contenir au moins 6 caractères' 
      });
      return;
    }

    try {
      await actions.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      // Réinitialiser le formulaire
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
        </div>
        <p className="text-gray-600">Gérez vos informations personnelles et vos paramètres de sécurité</p>
      </div>

      {/* Navigation par onglets */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <User size={16} />
              <span>Informations personnelles</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Lock size={16} />
              <span>Sécurité</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'subscription'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <CreditCard size={16} />
              <span>Abonnements</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('help')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'help'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <HelpCircle size={16} />
              <span>Aide</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="space-y-6">
        {activeTab === 'profile' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Informations personnelles</h3>
              <p className="mt-1 text-sm text-gray-500">
                Mettez à jour vos informations personnelles qui seront affichées dans l'application.
              </p>
            </div>
            
            <form onSubmit={handleProfileSubmit} className="px-6 py-4 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-gray-700">
                    Prénom
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    id="prenom"
                    required
                    value={profileData.prenom}
                    onChange={handleProfileChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700">
                    Nom
                  </label>
                  <input
                    type="text"
                    name="nom"
                    id="nom"
                    required
                    value={profileData.nom}
                    onChange={handleProfileChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <div className="mt-1 relative">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={state.auth.loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {state.auth.loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sauvegarde...
                    </div>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Modifier le mot de passe</h3>
              <p className="mt-1 text-sm text-gray-500">
                Assurez-vous d'utiliser un mot de passe fort et unique.
              </p>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="px-6 py-4 space-y-6">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">
                  Mot de passe actuel
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    name="current_password"
                    id="current_password"
                    required
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.current ? 
                      <EyeOff className="h-5 w-5 text-gray-400" /> : 
                      <Eye className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
                  Nouveau mot de passe
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    name="new_password"
                    id="new_password"
                    required
                    minLength="6"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.new ? 
                      <EyeOff className="h-5 w-5 text-gray-400" /> : 
                      <Eye className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Le mot de passe doit contenir au moins 6 caractères.
                </p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
                  Confirmer le nouveau mot de passe
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    name="confirm_password"
                    id="confirm_password"
                    required
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPasswords.confirm ? 
                      <EyeOff className="h-5 w-5 text-gray-400" /> : 
                      <Eye className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={state.auth.loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {state.auth.loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Modification...
                    </div>
                  ) : (
                    <>
                      <Lock size={16} className="mr-2" />
                      Modifier le mot de passe
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'subscription' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Gestion des abonnements</h3>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos abonnements Stripe, méthodes de paiement et factures.
              </p>
            </div>
            
            <div className="px-6 py-4">
              <SubscriptionManagement 
                refreshTrigger={refreshSubscriptions}
                onRefreshComplete={() => setRefreshSubscriptions(false)}
              />
            </div>
          </div>
        )}

        {activeTab === 'help' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Aide et support</h3>
              <p className="mt-1 text-sm text-gray-500">
                Accédez aux ressources d'aide et redécouvrez les fonctionnalités de l'application.
              </p>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* Onboarding */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <HelpCircle className="h-6 w-6 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-gray-900">Visite guidée</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Redécouvrez les fonctionnalités principales de Time Tracker grâce à notre présentation interactive.
                    </p>
                    <button
                      onClick={() => setShowOnboardingModal(true)}
                      className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Relancer la visite guidée
                    </button>
                  </div>
                </div>
              </div>


              {/* Version */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Save className="h-6 w-6 text-gray-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-gray-900">Version de l'application</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Time Tracker v1.0.0
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Dernière mise à jour : Aout 2025
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'onboarding */}
      <OnboardingModal 
        isOpen={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
      />
    </div>
  );
};

export default Profile; 